import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Select, Textarea } from '@components';
import { InviteUserData } from '../types';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteUserData) => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ isOpen, onClose, onInvite }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<InviteUserData>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
    message: '',
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onInvite(formData);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
        message: '',
      });
      onClose();
    } catch (error) {
      console.error('Invite failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'user',
      message: '',
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('users.inviteNewUser')}
          </h2>
          <Button variant="ghost" size="sm" onClick={handleClose} className="p-1 h-8 w-8">
            <span className="material-icons text-lg">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.emailAddress')}
            </label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('users.enterEmailAddress')}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.emailAddressDescription')}
            </p>
          </div>

          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.firstNameRequired')}
            </label>
            <Input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={e => setFormData({ ...formData, firstName: e.target.value })}
              placeholder={t('users.enterFirstName')}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.firstNameDescription')}
            </p>
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.lastNameRequired')}
            </label>
            <Input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={e => setFormData({ ...formData, lastName: e.target.value })}
              placeholder={t('users.enterLastName')}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.lastNameDescription')}
            </p>
          </div>

          <div>
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.roleRequired')}
            </label>
            <Select
              id="role"
              value={formData.role}
              onChange={value =>
                setFormData({ ...formData, role: value as InviteUserData['role'] })
              }
              options={[
                { value: 'user', label: t('users.user') },
                { value: 'manager', label: t('users.manager') },
                { value: 'admin', label: t('users.admin') },
              ]}
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.roleDescription')}
            </p>
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('users.personalMessage')}
            </label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={e => setFormData({ ...formData, message: e.target.value })}
              placeholder={t('users.personalMessagePlaceholder')}
              rows={3}
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('users.personalMessageDescription')}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={loading}>
              {t('users.cancel')}
            </Button>
            <Button type="submit" variant="gradient-multi" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
